import React, { useState } from 'react';
import { Button, Form, Input, notification } from 'antd';
import { extractDomainAndHomepage } from '../../utils';
import CustomIcon from '../../../components/CustomIcon';
import { useAddWebsiteIndexEntryMutation, useFetchWebsiteIndexEntriesQuery } from '../service';
import { useAddDomainAnalysisProcessMutation } from '../../process/service';
import { useAddSitemapImportProcessMutation } from '../../process/service';

type Props = {
    onFinish: (websiteGuid: string, foundSitemaps: string[]) => void;
    loading?: boolean;
};

export default function AddDomainStep({ onFinish, loading }: Props) {
    const [inputDomain, setInputDomain] = useState<string>('');
    const [inputDomainError, setInputDomainError] = useState<string>('');

    const { data: websiteIndexEntries, isLoading: isLoadingWebsiteIndexEntries } = useFetchWebsiteIndexEntriesQuery();
    const [addWebsiteIndexEntry, { isLoading: isAddingWebsiteIndexEntry }] = useAddWebsiteIndexEntryMutation();
    const [addSitemapImportProcess, { isLoading: isAddingSitemapImportProcess }] = useAddSitemapImportProcessMutation();
    const [addDomainAnalysisProcess, { isLoading: isAddingDomainAnalysisProcess }] = useAddDomainAnalysisProcessMutation();

    const existingWebsiteIndexEntries = websiteIndexEntries ? websiteIndexEntries.map((entry) => entry.domain) : [];

    const handleDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputDomainError('');
        setInputDomain(event.target.value);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const { domain, homepage, error } = extractDomainAndHomepage(inputDomain);

        if (error) {
            setInputDomainError(error);
            return;
        }

        if (domain && existingWebsiteIndexEntries.includes(domain)) {
            setInputDomainError('This domain is already added!');
            return;
        }

        let newWebsiteIndexEntry;

        if (domain && homepage) {
            try {
                newWebsiteIndexEntry = await addWebsiteIndexEntry({ domain: domain, homepage_url: homepage }).unwrap();
            } catch (error) {
                setInputDomainError(`Cannot access domain ${domain}!`);
                notification.error({
                    message: `We cannot access ${domain} in order to perform our test!`,
                    description: 'Either the domain is not online, or our tool is being blocked by your server.',
                });
                return;
            }
        }

        if (newWebsiteIndexEntry) {
            try {
                if (newWebsiteIndexEntry.sitemap_urls.length > 0) {
                    await addSitemapImportProcess({ w_guid: newWebsiteIndexEntry.guid, sitemap_urls: newWebsiteIndexEntry.sitemap_urls }).unwrap();
                }

                await addDomainAnalysisProcess({ w_guid: newWebsiteIndexEntry.guid }).unwrap();

                onFinish(newWebsiteIndexEntry.guid, newWebsiteIndexEntry.sitemap_urls);
            } catch (_) {
                notification.error({ message: 'Something went wrong!', description: 'Please try again or contact support!' });
            }
        }
    };

    const isAddingSomething = isAddingWebsiteIndexEntry || isAddingDomainAnalysisProcess || isAddingSitemapImportProcess;
    const isButtonLoading = loading || isAddingSomething;

    return (
        <Form onSubmit={handleSubmit}>
            <div className="deep-analysis-card">
                <div className="deep-analysis-card-icon domain">
                    <CustomIcon
                        name="deep-analysis"
                        color="white"
                    />
                </div>
                <h1>Deep Analysis</h1>
                <h2>All-in-One SEO audits to boost rankings across your domain</h2>
                <div className="deep-analysis-card-input-group domain">
                    <label htmlFor="domain">Add your domain below and click continue.</label>
                    <Form.Item
                        validateStatus={inputDomainError ? 'error' : 'validating'}
                        help={inputDomainError ? inputDomainError : ''}
                    >
                        <Input
                            id="domain"
                            placeholder="yourdomain.com"
                            value={inputDomain}
                            onChange={handleDomainChange}
                        />
                    </Form.Item>
                    <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        loading={isButtonLoading}
                        disabled={isLoadingWebsiteIndexEntries}
                    >
                        Continue
                    </Button>
                </div>
            </div>
        </Form>
    );
}
